import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { StudentJobDetail } from '@edtech-ui/core/models/student-job-management/get-all-student-job-details';
import { GetAllStudentJobs } from '@edtech-ui/core/models/student-job-management/get-all-student-jobs';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-candidate-saved-jobs',
	standalone: true,
	imports: [CommonModule, RouterModule, MatProgressSpinnerModule, MatTooltipModule],
	templateUrl: './candidate-saved-jobs.component.html',
	styleUrl: './candidate-saved-jobs.component.scss'
})
export class CandidateSavedJobsComponent {

	page = 1;
	limit = 10;
	savedCount = 0;
	subscription = new Subscription();
	isLoading: boolean = false;
	jobData: GetAllStudentJobs[] = [];
	totalPages: number = 0;
	isJobSave: boolean = false;
	isJobRemove: boolean = false;


	constructor(
		private router: Router,
		private studentJobService: StudentJobService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		const pendingJobId = localStorage.getItem('pending_save_job_id');
		if (pendingJobId) {
			localStorage.removeItem('pending_save_job_id');
			this.saveJobById(pendingJobId);
		} else {
			this.getAllStudentSaveJobs();
		}
	}

	saveJobById(jobId: string) {
		this.isJobSave = true;
		this.subscription.add(
			this.studentJobService.postStudentSaveJob(jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Data.message || 'Job saved successfully');
					} else {
						this.toastr.error(response.Message);
					}
					this.getAllStudentSaveJobs();
				}),
				finalize(() => {
					this.isJobSave = false;
				}),
				catchError(() => {
					this.toastr.error('Failed to save job');
					this.getAllStudentSaveJobs();
					return of(null);
				})
			).subscribe()
		);
	}


	getAllStudentSaveJobs() {
		this.isLoading = true;
		this.subscription.add(
			this.studentJobService.getAllStudentSaveJobs(this.page, this.limit).pipe(
				tap(response => {
					if (response.Success) {
						if (this.page === 1) {
							this.jobData = (response.Data.jobs || []).map(job => ({
								...job,
								locations: (job.locations || []).filter(location => location?.trim())
							})
							);
						} else {
							this.jobData = [...this.jobData, ...(response.Data.jobs || [])];
						}
						this.savedCount = response.Data.saved_count;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}


	loadMore() {
		if (this.page < this.totalPages && !this.isLoading) {
			this.page++;
			this.getAllStudentSaveJobs();
		}
	}


	goToJobsDetailPage(jobId: string) {
		this.router.navigate(['student-dashboard/jobs-detail-page', jobId]);
	}


	toggleSaveJob(job: GetAllStudentJobs) {
		if (job.is_saved) {
			this.deleteStudentSaveJob(job);
		} else {
			this.postStudentSaveJob(job);
		}
	}


	postStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobSave = true;

		this.subscription.add(
			this.studentJobService.postStudentSaveJob(job.job_id).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Data.message);
						this.getAllStudentSaveJobs();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobSave = false;
				})
			).subscribe()
		);
	}


	deleteStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobRemove = true;

		this.subscription.add(
			this.studentJobService.deleteStudentSaveJob(job.job_id).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Data.message);
						this.getAllStudentSaveJobs();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobRemove = false;
				})
			).subscribe()
		)
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}
